import type { ProximityZone } from '../types';

/** EMA weight for median-filtered RSSI (Lynx RssiProcessor default). */
export const RSSI_EMA_ALPHA = 0.55;

/** Median pre-filter window size (Lynx default). */
export const RSSI_MEDIAN_WINDOW = 3;

/** Max |raw − smoothed| (dBm) before a reading is treated as an outlier (Lynx). */
export const RSSI_OUTLIER_MAX_DELTA_DB = 12;

/**
 * Outliers in a row, all on the same side, that mean the signal really moved: the smoother restarts from them.
 * Without this a friend first heard faint (far off, or a phone in a pocket) who then came close was rejected as an
 * outlier on every scan, so their RSSI froze at the faint value for the rest of the session (2026-10-16 audit).
 */
export const RSSI_OUTLIER_RESET_RUN = 3;

/** Strong signal — device very close (dBm). */
export const ZONE_IMMEDIATE_MIN_DBM = -75;

/** Boundary between `near` and `far` (dBm). */
export const ZONE_NEAR_FLOOR_DBM = -92;

/** Hysteresis buffer (dBm) at zone boundaries (Lynx). */
export const ZONE_HYSTERESIS_DB = 2;

/** Must hold indicated zone this long before committing (Lynx). */
export const ZONE_DEBOUNCE_MS = 1_000;

/** Chronological smoothed RSSI history cap (Lynx). */
export const RSSI_HISTORY_MAX = 10;

/** Samples back for getRssiDelta when history is long enough (Lynx). */
export const RSSI_DELTA_WINDOW = 3;

/** Calibrated TX power at 1m (dBm); tune per hardware (Lynx). */
const TX_POWER_DBM = -59;

/** Path-loss exponent for distance estimate (Lynx). */
const PATH_LOSS_EXPONENT = 2.7;

const ZONE_IMMEDIATE_LEAVE_DBM = ZONE_IMMEDIATE_MIN_DBM - ZONE_HYSTERESIS_DB;
const ZONE_IMMEDIATE_ENTER_DBM = ZONE_IMMEDIATE_MIN_DBM + ZONE_HYSTERESIS_DB;
const ZONE_NEAR_LEAVE_DBM = ZONE_NEAR_FLOOR_DBM - ZONE_HYSTERESIS_DB;
const ZONE_NEAR_ENTER_DBM = ZONE_NEAR_FLOOR_DBM + ZONE_HYSTERESIS_DB;

export type RssiProcessorConfig = {
  alpha?: number;
  medianWindow?: number;
};

type DeviceState = {
  previousSmoothed: number | null;
  rawBuffer: number[];
  /** Consecutive rejected outliers, signed by side (+ above, − below the smoothed value). */
  outlierRun: number;
  /** Chronological smoothed values (oldest → newest), max RSSI_HISTORY_MAX. */
  history: number[];
  committedZone: ProximityZone | null;
  pendingZone: ProximityZone | null;
  pendingZoneSince: number;
};

export type RssiReadingResult = {
  smoothedRssi: number;
  estimatedDistance: number;
  zone: ProximityZone | null;
  rejectedOutlier: boolean;
};

export type RssiProcessorState = {
  devices: Map<string, DeviceState>;
  config: Required<RssiProcessorConfig>;
};

export function createRssiProcessorState(config: RssiProcessorConfig = {}): RssiProcessorState {
  return {
    devices: new Map(),
    config: {
      alpha: config.alpha ?? RSSI_EMA_ALPHA,
      medianWindow: config.medianWindow ?? RSSI_MEDIAN_WINDOW,
    },
  };
}

/** Port of Lynx RssiProcessor.addReading — pure, injectable clock. */
export function addRssiReading(
  state: RssiProcessorState,
  peerId: string,
  rawRssi: number,
  now: number,
): { state: RssiProcessorState; result: RssiReadingResult } {
  const devices = new Map(state.devices);
  let device = devices.get(peerId);
  if (!device) {
    device = {
      previousSmoothed: null,
      rawBuffer: [],
      outlierRun: 0,
      history: [],
      committedZone: null,
      pendingZone: null,
      pendingZoneSince: 0,
    };
    devices.set(peerId, device);
  }

  const { alpha, medianWindow } = state.config;

  if (device.previousSmoothed !== null) {
    const signedDelta = rawRssi - device.previousSmoothed;
    if (Math.abs(signedDelta) > RSSI_OUTLIER_MAX_DELTA_DB) {
      const side = signedDelta > 0 ? 1 : -1;
      const run = Math.sign(device.outlierRun) === side ? device.outlierRun + side : side;
      if (Math.abs(run) >= RSSI_OUTLIER_RESET_RUN) {
        // The signal really moved: restart the smoother here instead of rejecting it forever.
        device = { ...device, previousSmoothed: null, rawBuffer: [], outlierRun: 0 };
        devices.set(peerId, device);
      } else {
        devices.set(peerId, { ...device, outlierRun: run });
        return {
          state: { ...state, devices },
          result: {
            smoothedRssi: device.previousSmoothed,
            estimatedDistance: estimateDistanceFromRssi(device.previousSmoothed),
            zone: device.committedZone,
            rejectedOutlier: true,
          },
        };
      }
    }
  }

  const rawBuffer = [...device.rawBuffer, rawRssi];
  while (rawBuffer.length > medianWindow) {
    rawBuffer.shift();
  }

  const medianFiltered = median(rawBuffer);
  const prevSmoothed = device.previousSmoothed;
  const smoothedRssi =
    prevSmoothed === null ? medianFiltered : alpha * medianFiltered + (1 - alpha) * prevSmoothed;

  const zoneState = updateZoneState(
    {
      committedZone: device.committedZone,
      pendingZone: device.pendingZone,
      pendingZoneSince: device.pendingZoneSince,
    },
    smoothedRssi,
    now,
  );

  const history = [...device.history, smoothedRssi];
  while (history.length > RSSI_HISTORY_MAX) {
    history.shift();
  }

  devices.set(peerId, {
    previousSmoothed: smoothedRssi,
    rawBuffer,
    outlierRun: 0,
    history,
    ...zoneState,
  });

  return {
    state: { ...state, devices },
    result: {
      smoothedRssi,
      estimatedDistance: estimateDistanceFromRssi(smoothedRssi),
      zone: zoneState.committedZone,
      rejectedOutlier: false,
    },
  };
}

export function getRssiDeltaFromState(
  state: RssiProcessorState,
  peerId: string,
  n: number = RSSI_DELTA_WINDOW,
): number | null {
  const device = state.devices.get(peerId);
  if (!device || device.history.length <= n) {
    return null;
  }
  const current = device.history[device.history.length - 1]!;
  const past = device.history[device.history.length - 1 - n]!;
  return current - past;
}

function estimateDistanceFromRssi(smoothedRssi: number): number {
  if (smoothedRssi >= 0) {
    return 0;
  }
  return 10 ** ((TX_POWER_DBM - smoothedRssi) / (10 * PATH_LOSS_EXPONENT));
}

export function clearRssiDevice(state: RssiProcessorState, peerId: string): RssiProcessorState {
  const devices = new Map(state.devices);
  devices.delete(peerId);
  return { ...state, devices };
}

export function clearAllRssiDevices(state: RssiProcessorState): RssiProcessorState {
  return { ...state, devices: new Map() };
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

function smoothedRssiToZone(smoothedRssi: number): ProximityZone {
  if (smoothedRssi >= ZONE_IMMEDIATE_MIN_DBM) {
    return 'immediate';
  }
  if (smoothedRssi >= ZONE_NEAR_FLOOR_DBM) {
    return 'near';
  }
  return 'far';
}

function hysteresisIndicatedZone(smoothed: number, committed: ProximityZone): ProximityZone {
  switch (committed) {
    case 'immediate':
      if (smoothed >= ZONE_IMMEDIATE_LEAVE_DBM) {
        return 'immediate';
      }
      if (smoothed < ZONE_NEAR_LEAVE_DBM) {
        return 'far';
      }
      return 'near';
    case 'near':
      if (smoothed >= ZONE_IMMEDIATE_ENTER_DBM) {
        return 'immediate';
      }
      if (smoothed < ZONE_NEAR_LEAVE_DBM) {
        return 'far';
      }
      return 'near';
    case 'far':
      if (smoothed < ZONE_NEAR_ENTER_DBM) {
        return 'far';
      }
      if (smoothed >= ZONE_IMMEDIATE_ENTER_DBM) {
        return 'immediate';
      }
      return 'near';
  }
}

function updateZoneState(
  state: Pick<DeviceState, 'committedZone' | 'pendingZone' | 'pendingZoneSince'>,
  smoothedRssi: number,
  now: number,
): Pick<DeviceState, 'committedZone' | 'pendingZone' | 'pendingZoneSince'> {
  if (state.committedZone === null) {
    return {
      committedZone: smoothedRssiToZone(smoothedRssi),
      pendingZone: null,
      pendingZoneSince: 0,
    };
  }

  const indicated = hysteresisIndicatedZone(smoothedRssi, state.committedZone);

  if (indicated === state.committedZone) {
    return {
      committedZone: state.committedZone,
      pendingZone: null,
      pendingZoneSince: 0,
    };
  }

  if (state.pendingZone !== indicated) {
    return {
      committedZone: state.committedZone,
      pendingZone: indicated,
      pendingZoneSince: now,
    };
  }

  if (now - state.pendingZoneSince >= ZONE_DEBOUNCE_MS) {
    return {
      committedZone: indicated,
      pendingZone: null,
      pendingZoneSince: 0,
    };
  }

  return state;
}

/** Mutable wrapper used by the engine; delegates to pure functions. */
export class RssiProcessor {
  private state: RssiProcessorState;

  constructor(config: RssiProcessorConfig = {}) {
    this.state = createRssiProcessorState(config);
  }

  addReading(peerId: string, rawRssi: number, now: number): RssiReadingResult {
    const next = addRssiReading(this.state, peerId, rawRssi, now);
    this.state = next.state;
    return next.result;
  }

  clearDevice(peerId: string): void {
    this.state = clearRssiDevice(this.state, peerId);
  }

  clearAll(): void {
    this.state = clearAllRssiDevices(this.state);
  }

  getRssiDelta(peerId: string, n: number = RSSI_DELTA_WINDOW): number | null {
    return getRssiDeltaFromState(this.state, peerId, n);
  }
}
